/** Two nested branch pivots, baked into the existing instanced geometry.
 * xyz = attachment in tree space; w = angular compliance. Bark, leaves and
 * snow on a branch receive the same transform. No per-vertex leaf wobble.
 * -1 marks trunk vertices; 0 is reserved for non-tree material defaults. */
import * as THREE from 'three';

export type WindJoint = readonly [number, number, number, number];
export const TREE_STEM: WindJoint = [0, 0, 0, -1];
export const NO_JOINT: WindJoint = [0, 0, 0, 0];

export function windJoint(root: THREE.Vector3, tip: THREE.Vector3, radius: number, response = 1): WindJoint {
  // Slender connections yield more. Bound the stylized response so a very
  // thin twig cannot whip around even when multiple weather fronts overlap.
  const slenderness = root.distanceTo(tip) / Math.max(radius, .008);
  const compliance = THREE.MathUtils.clamp(slenderness * .018, .12, 1.35);
  // The shader takes the square root of compliance. Squaring this authored
  // family response gives an angular multiplier without another attribute.
  return [root.x, root.y, root.z, compliance * response * response];
}

export function paintTreeWind(g: THREE.BufferGeometry, branch: WindJoint = TREE_STEM,
  twig: WindJoint = NO_JOINT): THREE.BufferGeometry {
  const count = g.getAttribute('position').count;
  for (const [name, joint] of [['branchWind', branch], ['twigWind', twig]] as const) {
    const data = new Float32Array(count * 4);
    for (let i = 0; i < count; i++) data.set(joint, i * 4);
    g.setAttribute(name, new THREE.BufferAttribute(data, 4));
  }
  return g;
}

/** Shared by color/depth; gusts still come from the weather's existing field.
 * Rotations preserve lengths and attachment pivots. Child first, then parent,
 * then stem: the child attachment inherits exactly the parent's motion. */
export const TREE_WIND_GLSL = /* glsl */ `
attribute vec4 branchWind;
attribute vec4 twigWind;
vec3 hlRotateTree(vec3 p, vec3 pivot, vec3 axis, float angle) {
  vec3 v = p - pivot;
  float c = cos(angle), s = sin(angle);
  return pivot + v * c + cross(axis, v) * s + axis * dot(axis, v) * (1.0 - c);
}
float hlBranchAngle(vec4 joint, float load, float phase) {
  // A shared slow gust, with a small branch-dependent lag, not independent
  // oscillators. Calm means completely still, including terminal foliage.
  float lag = dot(joint.xyz, vec3(0.11, 0.07, 0.09));
  // Most of the response is changing motion, not a static windward lean.
  // The square-root response lets stout boughs breathe visibly while keeping
  // thin twigs more flexible, without multiplying their extreme response.
  float angle = sqrt(max(joint.w, 0.0)) * load * 0.075 *
    (0.38 + 0.52 * sin(phase - lag) + 0.10 * sin(phase * 1.73 - lag));
  return clamp(angle, -0.18, 0.18);
}
vec3 hlTreeMotion(vec3 p, vec2 direction, float speed, float detail, float phase) {
  vec3 axis = vec3(direction.y, 0.0, -direction.x);
  // Branches respond to ordinary breezes; the trunk remains much stiffer.
  // Both saturate under overlapping gusts. Zero wind still means no motion.
  float gain = min(max(uWindGain, 0.0), 3.0);
  float load = gain * speed / (6.0 + speed * 0.45);
  float branchLoad = gain * speed / (3.0 + speed * 0.65);
  if (detail > 0.0) {
    if (twigWind.w > 0.0) p = hlRotateTree(p, twigWind.xyz, axis, hlBranchAngle(twigWind, branchLoad, phase) * detail);
    if (branchWind.w > 0.0) p = hlRotateTree(p, branchWind.xyz, axis, hlBranchAngle(branchWind, branchLoad, phase) * detail);
  }
  float stemAngle = load * 0.0025 * 0.86;
  if (detail > 0.0) stemAngle += load * 0.0025 * 0.14 * sin(phase) * detail;
  return hlRotateTree(p, vec3(0.0), axis, stemAngle);
}
`;
